import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ServerModel } from 'app/models/serverModel'
import { isAdmin } from 'app/services/local/roleService'
import {
  editServerRequest,
  mapServerFields,
  toActionError,
} from 'app/containers/administration/serverQueries'

import { CheckboxField } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { TextField } from '@/ui/form/controls/TextField'

type Props = {
  editedServer: ServerModel
  onClose: (saved: boolean) => void
}

const isBlank = (value?: string) => value == null || value.trim() === ''

/**
 * Formularz edycji serwera - funkcyjny odpowiednik
 * `app/components/administration/editServer.tsx` (klasa na `ValidatorBase`).
 *
 * Zapis unieważnia zapytanie serwerów, bo lista pod modalem czyta ten sam klucz -
 * dokładnie to robił wcześniej `editServerContainer`.
 */
export const EditServerForm: React.FC<Props> = ({ editedServer, onClose }) => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const mutation = useMutation({ mutationFn: editServerRequest })

  const [server, setServer] = React.useState<ServerModel>(editedServer)
  const [submitted, setSubmitted] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const patch = (changes: Partial<ServerModel>) => {
    setServer((prev) => ({ ...prev, ...changes }))
    setErrorMessage(null)
  }

  const errors = {
    name: isBlank(server.name)
      ? t('administrationPanel-servers.validation-name-empty')
      : null,
    address: isBlank(server.address)
      ? t('administrationPanel-servers.validation-ipAddress-empty')
      : null,
    login: isBlank(server.login)
      ? t('administrationPanel-servers.validation-login-empty')
      : null,
    // Hasło jest wymagane tylko przy zakładaniu serwera; przy edycji puste znaczy
    // „nie zmieniaj".
    password:
      !server.id && isBlank(server.password)
        ? t('administrationPanel-servers.validation-password-empty')
        : null,
  }
  const isValid = Object.values(errors).every((message) => message == null)
  const error = (field: keyof typeof errors) =>
    submitted ? errors[field] : null

  const save = async () => {
    setSubmitted(true)
    if (!isValid) return

    let saved: ServerModel
    try {
      const response = await mutation.mutateAsync(server)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'servers'] })
      saved = mapServerFields(response.content.data)
    } catch (requestError) {
      saved = toActionError(requestError) as ServerModel
    }

    if (saved.error == null) {
      toast.success({
        title: t('administrationPanel-servers.server-edited'),
        text: server.name,
      })
      onClose(true)
    } else {
      setErrorMessage(`errorCode.${saved.error}`)
      toast.error({
        title: t('administrationPanel-servers.server-not-edited'),
        text: server.name,
      })
    }
  }

  const conflict = (code: string) =>
    errorMessage != null && errorMessage.includes(code)
      ? t(errorMessage)
      : null

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
        <TextField
          fullWidth
          label={t('administrationPanel-servers.name')}
          value={server.name ?? ''}
          onChange={(event) => patch({ name: event.target.value })}
          error={error('name') ?? conflict('SERVER_NAME_EXISTS')}
        />
        <TextField
          fullWidth
          readOnly={!isAdmin()}
          label={t('administrationPanel-servers.ip-address')}
          value={server.address ?? ''}
          onChange={(event) => patch({ address: event.target.value })}
          error={error('address') ?? conflict('SERVER_EXISTS')}
        />
        <TextField
          fullWidth
          label={t('administrationPanel-servers.localAddress')}
          value={server.localAddress ?? ''}
          onChange={(event) => patch({ localAddress: event.target.value })}
        />
        <TextField
          fullWidth
          label={t('administrationPanel-servers.login')}
          value={server.login ?? ''}
          onChange={(event) => patch({ login: event.target.value })}
          error={error('login')}
        />
        <TextField
          fullWidth
          type="password"
          label={t('administrationPanel-servers.password')}
          value={server.password ?? ''}
          onChange={(event) => patch({ password: event.target.value })}
          error={error('password')}
        />
        <TextField
          fullWidth
          label={t('administrationPanel-servers.backupLocation')}
          value={server.backupLocation ?? ''}
          onChange={(event) => patch({ backupLocation: event.target.value })}
        />
      </div>

      <CheckboxField
        id="isPrivate"
        checked={!!server.isPrivate}
        label={t('administrationPanel-servers.private')}
        onChange={(event) => {
          const isPrivate = (event.target as HTMLInputElement).checked
          patch(isPrivate ? { isPrivate, isDefault: false } : { isPrivate })
        }}
      />

      {isAdmin() && (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <CheckboxField
            id="isDefault"
            checked={!!server.isDefault}
            disabled={!!server.isPrivate}
            label={t('administrationPanel-servers.default-server')}
            onChange={(event) =>
              patch({ isDefault: (event.target as HTMLInputElement).checked })
            }
          />
          <span className="tw-text-xs tw-text-content-secondary">
            {t('administrationPanel-servers.default-server-hint')}
          </span>
        </div>
      )}

      <div className="tw-flex tw-justify-end tw-gap-2">
        <Button
          variant="primary"
          mode="outline"
          size="sm"
          onClick={() => onClose(false)}
        >
          {t('operation.cancel')}
        </Button>
        <Button variant="primary" size="sm" onClick={save}>
          {t('operation.save')}
        </Button>
      </div>
    </div>
  )
}
